import { useState, useEffect, useCallback } from 'react'
import api from '../api/client'
// logger removed — fetchUser handles errors silently

interface User {
  user_id: string
  email: string
  email_verified: boolean
  persona: string
  plan_tier: string
  first_name?: string | null
  last_name?: string | null
  notify_job_completed: boolean
  created_at: string
}

// Cache the last-known user in localStorage so we can render auth-aware
// UI (TopNav, etc.) on first paint without making a network request.
// Anonymous visitors get nothing here, so the cache stays empty and the
// page renders the "signed-out" state immediately.
const USER_CACHE_KEY = 'fth_user_cache'

function readCachedUser(): User | null {
  try {
    if (typeof localStorage === 'undefined') return null
    const raw = localStorage.getItem(USER_CACHE_KEY)
    return raw ? (JSON.parse(raw) as User) : null
  } catch {
    return null
  }
}

function writeCachedUser(user: User | null): void {
  try {
    if (typeof localStorage === 'undefined') return
    if (user) localStorage.setItem(USER_CACHE_KEY, JSON.stringify(user))
    else localStorage.removeItem(USER_CACHE_KEY)
  } catch {
    /* quota / private mode — ignore */
  }
}


export function useAuth() {
  // Initial state from cache: instant auth-aware render, zero network.
  const [user, setUser] = useState<User | null>(() => readCachedUser())
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchUser = useCallback(async () => {
    setLoading(true)
    try {
      const userData = await api.getMe()
      setUser(userData)
      writeCachedUser(userData)
      setError(null)
    } catch {
      // Not authenticated. Drop the cache so the next render reflects
      // signed-out state correctly.
      setUser(null)
      writeCachedUser(null)
    } finally {
      setLoading(false)
    }
  }, [])

  // We only validate auth when there's reason to believe the visitor
  // might be logged in:
  //   1. A JWT token in localStorage (standard sign-in flow), OR
  //   2. A cached user from a previous session
  //
  // Anonymous-first-visit visitors hit neither path → zero /auth/me +
  // /entitlements/usage requests on the landing critical path. This
  // is the common case (Lighthouse, Google crawlers, organic visitors)
  // and was the source of the ~700ms LCP penalty.
  //
  // The validation is also deferred via requestIdleCallback so even
  // returning users don't block first paint on the round-trip.
  useEffect(() => {
    const hasToken = typeof localStorage !== 'undefined' && !!localStorage.getItem('fth_token')
    const hasCachedUser = !!user
    if (!hasToken && !hasCachedUser) {
      // Anonymous — render signed-out UI, never make the call.
      return
    }
    let cancelled = false
    const run = () => { if (!cancelled) fetchUser() }
    const ric = (window as unknown as { requestIdleCallback?: (cb: () => void, opts?: { timeout?: number }) => number }).requestIdleCallback
    if (typeof ric === 'function') {
      const handle = ric(run, { timeout: 2000 })
      return () => {
        cancelled = true
        const cic = (window as unknown as { cancelIdleCallback?: (h: number) => void }).cancelIdleCallback
        if (typeof cic === 'function') cic(handle)
      }
    }
    const t = window.setTimeout(run, 0)
    return () => {
      cancelled = true
      window.clearTimeout(t)
    }
    // We deliberately depend only on fetchUser so this fires once per
    // mount, not whenever `user` changes. Re-validation post-login
    // happens through login()/register()/logout() explicitly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchUser])

  const login = async (email: string, password: string) => {
    setLoading(true)
    setError(null)
    try {
      await api.login(email, password)
      await fetchUser()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed')
      throw err
    } finally {
      setLoading(false)
    }
  }

  const register = async (email: string, password: string, persona: string) => {
    setLoading(true)
    setError(null)
    try {
      await api.register(email, password, persona)
      await fetchUser()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed')
      throw err
    } finally {
      setLoading(false)
    }
  }

  const logout = async () => {
    await api.logout()
    setUser(null)
    writeCachedUser(null)
    // NOTE: we intentionally KEEP 'fth_active_role' so a returning recruiter lands back in
    // recruiter mode. Logout routing goes to the right marketing page (see TopNav.handleLogout).
  }

  const planTier = user?.plan_tier || 'free'
  const isAuthenticated = !!user
  const PRO_TIERS = ['pro', 'power', 'enterprise', 'recruiter']

  return {
    user,
    loading,
    error,
    login,
    register,
    logout,
    fetchUser,
    isAuthenticated,
    isRecruiter: user?.persona === 'recruiter',
    isJobSeeker: user?.persona === 'job_seeker',
    planTier,
    // Tier checks — use these instead of raw planTier comparisons
    isRegistered: isAuthenticated,                          // has an account
    isPro: isAuthenticated && PRO_TIERS.includes(planTier), // active subscription
    isFree: !isAuthenticated || planTier === 'free',        // not registered OR no subscription
  }
}
